import { ApiError } from "@/services/api.ts";
import { LOAD_GROUPS_FAILED, SAVE_FAILED, SETTINGS_CONFLICT, SETTINGS_UNAVAILABLE } from "@/utils/alertCopy.ts";
import type { NotificationGroup, NotificationGroupInput } from "@/types";

const ADDRESS_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GROUP_FIELDS = ["name", "emails", "rules"] as const;

type GroupField = (typeof GROUP_FIELDS)[number];

/** One rejected field of a 422, as the BFF passes ip-shield's on: e.g. loc ["body", "groups", 3, "emails", 7]. */
export interface Rejection {
  loc: (string | number)[];
  msg: string;
}

/** Messages for the edited group's fields; `other` holds rejections that name none of them. */
export interface GroupFieldErrors {
  name?: string;
  emails?: string;
  rules?: string;
  other: string[];
}

/** The addresses in the recipients box: one per line, and a pasted "a, b; c" splits the same way. */
export function parseRecipients(text: string): string[] {
  return text.split(/[\s,;]+/).filter((entry) => entry !== "");
}

/** The first entry not shaped like an address, for the hint under the box. ip-shield has the final say. */
export function firstUnlikelyAddress(text: string): string | null {
  return parseRecipients(text).find((entry) => !ADDRESS_SHAPE.test(entry)) ?? null;
}

export function toGroupInput(group: NotificationGroup): NotificationGroupInput {
  return { id: group.id, name: group.name, emails: group.emails, rules: group.rules };
}

function isRejection(value: unknown): value is Rejection {
  const candidate = value as Partial<Rejection> | null;
  return Array.isArray(candidate?.loc) && typeof candidate?.msg === "string";
}

/** The rejected fields of a 422; empty for any other error. */
export function rejections(error: unknown): Rejection[] {
  if (!(error instanceof ApiError) || error.status !== 422) return [];
  try {
    const detail: unknown = (JSON.parse(error.message) as { detail?: unknown }).detail;
    return Array.isArray(detail) ? detail.filter(isRejection) : [];
  } catch {
    return [];
  }
}

function fieldOf(loc: Rejection["loc"], groupIndex: number): { field: GroupField; item: unknown } | null {
  const [list, index, field, item] = loc[0] === "body" ? loc.slice(1) : loc;
  if (list !== "groups" || index !== groupIndex) return null;
  const known = GROUP_FIELDS.find((name) => name === field);
  return known ? { field: known, item } : null;
}

/** Each rejection beside the field it names; an address rejection says which address. */
export function groupFieldErrors(error: unknown, groupIndex: number, emails: readonly string[]): GroupFieldErrors {
  const result: GroupFieldErrors = { other: [] };
  for (const { loc, msg } of rejections(error)) {
    const target = fieldOf(loc, groupIndex);
    if (!target) {
      result.other.push(msg);
    } else if (
      target.field === "emails" &&
      typeof target.item === "number" &&
      target.item >= 0 &&
      target.item < emails.length
    ) {
      result.emails ??= `${emails[target.item]}: ${msg}`;
    } else {
      result[target.field] ??= msg;
    }
  }
  return result;
}

/** The sentence for a failed save; null when a 422's field messages say it instead. */
export function saveErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.status === 409) return SETTINGS_CONFLICT;
  if (error instanceof ApiError && error.status === 503) return SETTINGS_UNAVAILABLE;
  return rejections(error).length > 0 ? null : SAVE_FAILED;
}

export function loadErrorMessage(error: unknown): string {
  return error instanceof ApiError && error.status === 503 ? SETTINGS_UNAVAILABLE : LOAD_GROUPS_FAILED;
}
